import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowLeftRight,
  ArrowUpDown,
  ChevronDown,
  Clock,
  ExternalLink,
  FlaskConical,
  Gift,
  Globe,
  Package,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  ShieldCheck,
  Truck,
  TrendingUp,
  X,
} from "lucide-react";
import { CarrierIssuesTab } from "./carrier-issues-tab";
import { ReplayCreationActionsTab } from "./replay-creation-actions-tab";
import { useState, useMemo, useCallback, memo, useRef, useEffect } from "react";
import {
  useReactivateSuperAdminShop,
  useRefreshSuperAdminShopStats,
  useRefreshSuperAdminShops,
  useSuperAdminShopDetail,
  useSuperAdminShops,
  useSuperAdminShopsCatalog,
  useUpdateSuperAdminShopMeta,
} from "@/api/super-admin/use-super-admin-shops";
import {
  useProbeAllCatalogCounts,
  useProbeCatalogShopCount,
} from "@/api/super-admin/use-super-admin-catalog-sync";
import { useSyncCancelledReturnRequestsStats } from "@/api/super-admin/use-super-admin-statistics";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@babackapp/ui/components/sheet";

// ─── Types ────────────────────────────────────────────────────────────────────

type SortKey =
  | "name"
  | "totalOrders"
  | "ordersLast30Days"
  | "ordersLast7Days"
  | "ordersLast24Hours"
  | "totalReturnRequests"
  | "returnRequestsLast30Days"
  | "returnRequestsLast7Days"
  | "returnRequestsLast24Hours"
  | "lastActivityAt"
  | "membersCount"
  | "catalogProductCount";
type SortDir = "asc" | "desc";

type ClientStatus = "prospect" | "onboarding" | "active" | "paused" | "churned";

interface ShopRowData {
  id: string;
  name: string;
  slug: string;
  planCode: string | null;
  isTestShop: boolean | null;
  clientStatus: string | null;
  isIncludedInStats: boolean | null;
  clientName: string | null;
  notes: string | null;
  csm: string | null;
  internalLabel: string | null;
  totalOrders: number;
  ordersLast30Days: number;
  ordersLast7Days: number;
  ordersLast24Hours: number;
  totalReturnRequests: number;
  returnRequestsLast30Days: number;
  returnRequestsLast7Days: number;
  returnRequestsLast24Hours: number;
  lastActivityAt: number | null;
  // Feature flags
  hasExchanges: boolean;
  hasGiftCode: boolean;
  hasWorkflowLogistician: boolean;
  activeIntegrations: string[];
  catalogProductCount: number | null;
  catalogProductCountProbedAt: number | null;
  isCatalogProductCountApproximate: boolean | null;
  catalogArchivedProductCount: number | null;
  isCatalogArchivedProductCountApproximate: boolean | null;
  loadedCatalogProductCount: number | null;
  loadedCatalogArchivedProductCount: number | null;
  planProductRecommendation: boolean | null;
  isActive: boolean | null;
}

interface SelectedShop {
  id: string;
  name: string;
  slug: string;
  isTestShop: boolean | null;
  clientStatus: ClientStatus | null;
  isIncludedInStats: boolean | null;
  notes: string | null;
  clientName: string | null;
  csm: string | null;
  internalLabel: string | null;
  hasExchanges: boolean;
  hasGiftCode: boolean;
  hasWorkflowLogistician: boolean;
  activeIntegrations: string[];
  // Period stats (from list overview)
  totalOrders: number;
  totalReturnRequests: number;
  ordersLast30Days: number;
  ordersLast7Days: number;
  ordersLast24Hours: number;
  returnRequestsLast30Days: number;
  returnRequestsLast7Days: number;
  returnRequestsLast24Hours: number;
  planProductRecommendation: boolean | null;
  isActive: boolean | null;
  lastActivityAt: number | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(ts: number | null): string {
  if (!ts) return "—";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(ts));
}

function formatRelative(ts: number): string {
  const diffMs = Date.now() - ts;
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);

  if (diffMins < 1) return "à l'instant";
  if (diffMins < 60) return `il y a ${diffMins} min`;
  if (diffHours < 24) return `il y a ${diffHours}h`;
  return `il y a ${Math.floor(diffHours / 24)}j`;
}

const CLIENT_STATUS_LABELS: Record<
  ClientStatus,
  { label: string; color: string }
> = {
  prospect: { label: "Prospect", color: "bg-gray-100 text-gray-600" },
  onboarding: { label: "Onboarding", color: "bg-blue-100 text-blue-700" },
  active: { label: "Actif", color: "bg-green-100 text-green-700" },
  paused: { label: "Pausé", color: "bg-yellow-100 text-yellow-700" },
  churned: { label: "Churné", color: "bg-red-100 text-red-700" },
};

// ─── Integrations registry ────────────────────────────────────────────────────

export const INTEGRATIONS: Record<
  string,
  { label: string; color: string; pillColor: string }
> = {
  brevo: {
    label: "Brevo",
    color: "bg-teal-100 text-teal-700 border-teal-200",
    pillColor: "bg-teal-600",
  },
  fastmag: {
    label: "Fastmag",
    color: "bg-slate-100 text-slate-700 border-slate-200",
    pillColor: "bg-slate-500",
  },
  glopal: {
    label: "Glopal",
    color: "bg-green-100 text-green-700 border-green-200",
    pillColor: "bg-green-600",
  },
  gorgias: {
    label: "Gorgias",
    color: "bg-purple-100 text-purple-700 border-purple-200",
    pillColor: "bg-purple-600",
  },
  klaviyo: {
    label: "Klaviyo",
    color: "bg-emerald-100 text-emerald-700 border-emerald-200",
    pillColor: "bg-emerald-600",
  },
  oneStock: {
    label: "OneStock",
    color: "bg-cyan-100 text-cyan-700 border-cyan-200",
    pillColor: "bg-cyan-600",
  },
  shippingBo: {
    label: "ShippingBo",
    color: "bg-orange-100 text-orange-700 border-orange-200",
    pillColor: "bg-orange-500",
  },
  splio: {
    label: "Splio",
    color: "bg-pink-100 text-pink-700 border-pink-200",
    pillColor: "bg-pink-600",
  },
  stripe: {
    label: "Stripe",
    color: "bg-indigo-100 text-indigo-700 border-indigo-200",
    pillColor: "bg-indigo-600",
  },
  y2: {
    label: "Y2",
    color: "bg-yellow-100 text-yellow-700 border-yellow-200",
    pillColor: "bg-yellow-500",
  },
  zendesk: {
    label: "Zendesk",
    color: "bg-lime-100 text-lime-700 border-lime-200",
    pillColor: "bg-lime-600",
  },
};

// Feature filter keys: features + integration keys
type FeatureKey =
  | "exchanges"
  | "giftCode"
  | "logistician"
  | keyof typeof INTEGRATIONS;

// ─── Sub-components ───────────────────────────────────────────────────────────

function ActivityBadge({ last7Days }: { last7Days: number }) {
  if (last7Days >= 50)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
        <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
        Actif
      </span>
    );
  if (last7Days >= 5)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
        <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
        Modéré
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      Faible
    </span>
  );
}

function PercentBar({ pct }: { pct: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-violet-400"
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <span className="text-xs tabular-nums text-muted-foreground w-10 text-right">
        {pct.toFixed(1)}%
      </span>
    </div>
  );
}

function countryFlag(code: string): string {
  if (!code || code.length !== 2) return "🏳";
  return String.fromCodePoint(
    ...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
  );
}

// ─── Feature KPI ─────────────────────────────────────────────────────────────

const FEATURE_COLORS = {
  blue: {
    bar: "bg-blue-400",
    text: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-100",
  },
  violet: {
    bar: "bg-violet-400",
    text: "text-violet-600",
    bg: "bg-violet-50",
    border: "border-violet-100",
  },
  orange: {
    bar: "bg-orange-400",
    text: "text-orange-600",
    bg: "bg-orange-50",
    border: "border-orange-100",
  },
};

function FeatureKpi({
  icon,
  label,
  count,
  total,
  pct,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  total: number;
  pct: number;
  color: keyof typeof FEATURE_COLORS;
}) {
  const c = FEATURE_COLORS[color];
  return (
    <div className={`rounded-lg border ${c.border} ${c.bg} p-4 space-y-2`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-xs font-medium text-muted-foreground">
            {label}
          </span>
        </div>
        <span className={`text-xs font-semibold tabular-nums ${c.text}`}>
          {pct}%
        </span>
      </div>
      <div className="flex items-end justify-between gap-2">
        <span className="text-2xl font-bold tabular-nums">{count}</span>
        <span className="text-xs text-muted-foreground mb-0.5">
          / {total} shops
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-black/10 overflow-hidden">
        <div
          className={`h-full rounded-full ${c.bar} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─── Feature Badge ────────────────────────────────────────────────────────────

function FeatureBadge({
  icon,
  label,
  active,
  activeColor,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  activeColor: string;
}) {
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
        active
          ? activeColor
          : "bg-muted/50 text-muted-foreground/50 border-transparent",
      ].join(" ")}
    >
      {icon}
      {label}
    </span>
  );
}

// ─── Toggle ───────────────────────────────────────────────────────────────────

function Toggle({ on, color }: { on: boolean; color: "amber" | "green" }) {
  const trackOn = color === "amber" ? "bg-amber-500" : "bg-green-500";
  return (
    <div
      className={[
        "relative h-5 w-9 rounded-full transition-colors shrink-0 pointer-events-none",
        on ? trackOn : "bg-muted-foreground/30",
      ].join(" ")}
    >
      <span
        className={[
          "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform",
          on ? "translate-x-4" : "translate-x-0.5",
        ].join(" ")}
      />
    </div>
  );
}

// ─── Shop Meta Editor ─────────────────────────────────────────────────────────

function ShopMetaEditor({
  shop,
  clientNames,
  csmNames,
  onSaved,
}: {
  shop: SelectedShop;
  clientNames: string[];
  csmNames: string[];
  onSaved: (updated: Partial<SelectedShop>) => void;
}) {
  const updateMutation = useUpdateSuperAdminShopMeta();

  const [isTestShop, setIsTestShop] = useState(shop.isTestShop ?? false);
  const [clientStatus, setClientStatus] = useState<string>(
    shop.clientStatus ?? "",
  );
  const [isIncludedInStats, setIsIncludedInStats] = useState(
    shop.isIncludedInStats ?? true,
  );
  const [planProductRecommendation, setPlanProductRecommendation] = useState(
    shop.planProductRecommendation ?? false,
  );
  const [clientName, setClientName] = useState(shop.clientName ?? "");
  const [csm, setCsm] = useState(shop.csm ?? "");
  const [internalLabel, setInternalLabel] = useState(shop.internalLabel ?? "");
  const [notes, setNotes] = useState(shop.notes ?? "");
  const [saved, setSaved] = useState(false);

  async function handleSave() {
    const result = await updateMutation.mutateAsync({
      params: { path: { shopId: shop.id } },
      body: {
        isTestShop: isTestShop || null,
        clientStatus: clientStatus || null,
        // null = "include by default"; false = explicitly excluded
        isIncludedInStats: isTestShop
          ? false
          : isIncludedInStats
            ? null
            : false,
        clientName: clientName.trim() || null,
        csm: csm || null,
        internalLabel: internalLabel || null,
        notes: notes || null,
        planProductRecommendation: planProductRecommendation || null,
      },
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    onSaved({
      isTestShop: result?.isTestShop ?? null,
      clientStatus: result?.clientStatus ?? null,
      isIncludedInStats: result?.isIncludedInStats ?? null,
      clientName: result?.clientName ?? null,
      notes: result?.notes ?? null,
      csm: result?.csm ?? null,
      internalLabel: result?.internalLabel ?? null,
      planProductRecommendation: result?.planProductRecommendation ?? null,
    });
  }

  return (
    <section className="border-t px-6 py-5 space-y-4">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
        Infos internes
      </h3>

      <div className="grid grid-cols-2 gap-4">
        {/* isTestShop */}
        <div
          role="switch"
          aria-checked={isTestShop}
          tabIndex={0}
          onClick={() => setIsTestShop((v) => !v)}
          onKeyDown={(e) =>
            e.key === " " || e.key === "Enter" ? setIsTestShop((v) => !v) : null
          }
          className="flex items-center justify-between gap-2 rounded-md border p-3 cursor-pointer hover:bg-muted/30 transition-colors select-none"
        >
          <div>
            <div className="text-sm font-medium">Shop de test</div>
            <div className="text-xs text-muted-foreground">
              Exclut des stats par défaut
            </div>
          </div>
          <Toggle on={isTestShop} color="amber" />
        </div>

        {/* isIncludedInStats */}
        <div
          role="switch"
          aria-checked={!isTestShop && isIncludedInStats}
          tabIndex={isTestShop ? -1 : 0}
          onClick={() => !isTestShop && setIsIncludedInStats((v) => !v)}
          onKeyDown={(e) =>
            !isTestShop && (e.key === " " || e.key === "Enter")
              ? setIsIncludedInStats((v) => !v)
              : null
          }
          className={[
            "flex items-center justify-between gap-2 rounded-md border p-3 transition-colors select-none",
            isTestShop
              ? "opacity-40 cursor-not-allowed"
              : "cursor-pointer hover:bg-muted/30",
          ].join(" ")}
        >
          <div>
            <div className="text-sm font-medium">Inclus dans les stats</div>
            <div className="text-xs text-muted-foreground">
              Compte dans les agrégats
            </div>
          </div>
          <Toggle on={!isTestShop && isIncludedInStats} color="green" />
        </div>

        {/* planProductRecommendation */}
        <div
          role="switch"
          aria-checked={planProductRecommendation}
          tabIndex={0}
          onClick={() => setPlanProductRecommendation((v) => !v)}
          onKeyDown={(e) =>
            e.key === " " || e.key === "Enter"
              ? setPlanProductRecommendation((v) => !v)
              : null
          }
          className="flex items-center justify-between gap-2 rounded-md border p-3 cursor-pointer hover:bg-muted/30 transition-colors select-none col-span-2"
        >
          <div>
            <div className="text-sm font-medium">Reco produit</div>
            <div className="text-xs text-muted-foreground">
              Active plan.productRecommendation
            </div>
          </div>
          <Toggle on={planProductRecommendation} color="green" />
        </div>

        {/* clientName */}
        <div className="space-y-1 col-span-2">
          <label className="text-xs font-medium text-muted-foreground">
            Nom du client
          </label>
          <input
            type="text"
            list={`client-names-${shop.id}`}
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="Ex. Maison Dupont, Groupe XYZ…"
            className="w-full rounded-md border bg-background px-2 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <datalist id={`client-names-${shop.id}`}>
            {clientNames.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
          {clientName && !clientNames.includes(clientName.trim()) && (
            <p className="text-[10px] text-violet-600">Nouveau client</p>
          )}
        </div>

        {/* clientStatus */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            Statut client
          </label>
          <select
            value={clientStatus}
            onChange={(e) => setClientStatus(e.target.value)}
            className="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="">— Non défini</option>
            <option value="prospect">Prospect</option>
            <option value="onboarding">Onboarding</option>
            <option value="active">Actif</option>
            <option value="paused">Pausé</option>
            <option value="churned">Churné</option>
          </select>
        </div>

        {/* CSM */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            CSM
          </label>
          <input
            type="text"
            list={`csm-names-${shop.id}`}
            value={csm}
            onChange={(e) => setCsm(e.target.value)}
            placeholder="Nom du CSM…"
            className="w-full rounded-md border bg-background px-2 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <datalist id={`csm-names-${shop.id}`}>
            {csmNames.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
        </div>

        {/* internalLabel */}
        <div className="space-y-1 col-span-2">
          <label className="text-xs font-medium text-muted-foreground">
            Label interne
          </label>
          <input
            type="text"
            value={internalLabel}
            onChange={(e) => setInternalLabel(e.target.value)}
            placeholder="Ex. Partenaire premium, Migration en cours…"
            className="w-full rounded-md border bg-background px-2 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>

        {/* Notes */}
        <div className="space-y-1 col-span-2">
          <label className="text-xs font-medium text-muted-foreground">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Notes libres sur ce shop…"
            className="w-full rounded-md border bg-background px-2 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={updateMutation.isPending}
          className={[
            "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50",
            saved
              ? "bg-green-600 text-white"
              : "bg-violet-600 text-white hover:bg-violet-700",
          ].join(" ")}
        >
          <Save className="h-3 w-3" />
          {updateMutation.isPending
            ? "Enregistrement…"
            : saved
              ? "Enregistré ✓"
              : "Enregistrer"}
        </button>
      </div>
    </section>
  );
}

// ─── Shop Detail Drawer ───────────────────────────────────────────────────────

function ShopDetailDrawer({
  shop,
  clientNames,
  csmNames,
  onClose,
  onMetaSaved,
}: {
  shop: SelectedShop | null;
  clientNames: string[];
  csmNames: string[];
  onClose: () => void;
  onMetaSaved: (
    shopId: string,
    updated: Partial<SelectedShop>,
  ) => Promise<void>;
}) {
  const navigate = useNavigate();
  const { data, isPending, isError, refetch } = useSuperAdminShopDetail(
    shop?.id ?? null,
  );
  const refreshMutation = useRefreshSuperAdminShopStats();
  const reactivateMutation = useReactivateSuperAdminShop();
  const syncCancelledMutation = useSyncCancelledReturnRequestsStats();

  async function handleReactivate() {
    if (!shop) return;
    await reactivateMutation.mutateAsync({
      params: { path: { shopId: shop.id } },
    });
    await onMetaSaved(shop.id, { isActive: true });
  }

  const isOpen = !!shop;

  async function handleShopRefresh() {
    if (!shop) return;
    await refreshMutation.mutateAsync({
      params: { path: { shopId: shop.id } },
    });
    await refetch();
  }

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[65vw] flex flex-col gap-0 p-0 overflow-hidden"
      >
        {/* SheetTitle must always be in the DOM while SheetContent is mounted */}
        <SheetTitle className="sr-only">
          {shop?.name ?? "Détail shop"}
        </SheetTitle>

        {shop && (
          <>
            {/* Header */}
            <SheetHeader className="px-6 py-4 border-b flex-row items-center justify-between space-y-0 shrink-0">
              <div className="flex flex-col gap-0.5 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-base font-semibold leading-tight truncate">
                    {shop.name}
                  </span>
                  {shop.isTestShop && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 shrink-0">
                      <FlaskConical className="h-3 w-3" />
                      Test
                    </span>
                  )}
                  {shop.isActive === false && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 shrink-0">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                      Inactif
                    </span>
                  )}
                  {shop.clientStatus && (
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium shrink-0 ${CLIENT_STATUS_LABELS[shop.clientStatus]?.color ?? ""}`}
                    >
                      {CLIENT_STATUS_LABELS[shop.clientStatus]?.label ??
                        shop.clientStatus}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground font-mono">
                    {shop.slug}
                  </span>
                  {shop.lastActivityAt && (
                    <span className="text-xs text-muted-foreground">
                      Activité : {formatDate(shop.lastActivityAt)}
                    </span>
                  )}
                  {data?.statsComputedAt && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatRelative(data.statsComputedAt)}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-4">
                <button
                  onClick={handleShopRefresh}
                  disabled={refreshMutation.isPending}
                  className="flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-muted transition-colors disabled:opacity-50"
                  title="Recalculer les stats de ce shop"
                >
                  <RefreshCw
                    className={`h-3 w-3 ${refreshMutation.isPending ? "animate-spin" : ""}`}
                  />
                  {refreshMutation.isPending ? "Calcul…" : "Rafraîchir"}
                </button>
                <button
                  onClick={() =>
                    syncCancelledMutation.mutate({
                      params: { query: { shopId: shop.id } },
                    })
                  }
                  disabled={syncCancelledMutation.isPending}
                  className="flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-muted transition-colors disabled:opacity-50"
                  title="Backfill isCancelled dans les stats pour ce shop (idempotent)"
                >
                  <RotateCcw
                    className={`h-3 w-3 ${syncCancelledMutation.isPending ? "animate-spin" : ""}`}
                  />
                  {syncCancelledMutation.isPending
                    ? "Sync…"
                    : syncCancelledMutation.isSuccess
                      ? `✓ ${(syncCancelledMutation.data as { updated: number } | undefined)?.updated ?? 0} màj`
                      : "Sync annulations"}
                </button>
                {shop.isActive === false && (
                  <button
                    onClick={handleReactivate}
                    disabled={reactivateMutation.isPending}
                    className="flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 transition-colors disabled:opacity-50"
                    title="Réactiver ce shop (isActive=true, lastActivityAt=now)"
                  >
                    <TrendingUp className="h-3 w-3" />
                    {reactivateMutation.isPending
                      ? "Réactivation…"
                      : "Réactiver"}
                  </button>
                )}
                <button
                  onClick={() => navigate(`/shops/${shop.id}`)}
                  className="flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 transition-colors"
                >
                  <ExternalLink className="h-3 w-3" />
                  Dashboard
                </button>
                <button
                  onClick={onClose}
                  className="rounded-sm opacity-70 hover:opacity-100 transition-opacity ml-1"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </SheetHeader>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
              {isPending && (
                <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
                  Chargement…
                </div>
              )}

              {isError && (
                <div className="flex items-center justify-center py-20 text-destructive text-sm">
                  Erreur lors du chargement
                </div>
              )}

              {data && (
                <div className="p-6 space-y-6">
                  {/* Period stat cards */}
                  <div className="grid grid-cols-4 gap-3">
                    <StatPanel
                      period="Total"
                      orders={data.totalOrders}
                      returns={data.totalReturnRequests}
                    />
                    <StatPanel
                      period="30 jours"
                      orders={shop.ordersLast30Days}
                      returns={shop.returnRequestsLast30Days}
                      color="amber"
                    />
                    <StatPanel
                      period="7 jours"
                      orders={shop.ordersLast7Days}
                      returns={shop.returnRequestsLast7Days}
                      color="emerald"
                    />
                    <StatPanel
                      period="24h"
                      orders={shop.ordersLast24Hours}
                      returns={shop.returnRequestsLast24Hours}
                      color="blue"
                    />
                  </div>

                  {/* Feature flags + integrations */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <FeatureBadge
                        icon={<ArrowLeftRight className="h-3 w-3" />}
                        label="Échanges"
                        active={shop.hasExchanges}
                        activeColor="bg-blue-100 text-blue-700 border-blue-200"
                      />
                      <FeatureBadge
                        icon={<Gift className="h-3 w-3" />}
                        label="Avoirs"
                        active={shop.hasGiftCode}
                        activeColor="bg-violet-100 text-violet-700 border-violet-200"
                      />
                      <FeatureBadge
                        icon={<Package className="h-3 w-3" />}
                        label="Workflow logisticien"
                        active={shop.hasWorkflowLogistician}
                        activeColor="bg-orange-100 text-orange-700 border-orange-200"
                      />
                    </div>
                    {shop.activeIntegrations.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {shop.activeIntegrations.map((key) => {
                          const info = INTEGRATIONS[key];
                          return (
                            <span
                              key={key}
                              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${info?.color ?? "bg-muted text-muted-foreground border-transparent"}`}
                            >
                              {info?.label ?? key}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Two columns: countries + carriers */}
                  <div className="grid grid-cols-2 gap-6">
                    {/* By Country */}
                    <section>
                      <h3 className="flex items-center gap-2 text-sm font-semibold mb-3">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        Retours par pays
                        {data.byCountry.length > 0 && (
                          <span className="text-xs text-muted-foreground font-normal">
                            top {data.byCountry.length}
                          </span>
                        )}
                      </h3>
                      {data.byCountry.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          Aucune donnée
                        </p>
                      ) : (
                        <div className="space-y-2 max-h-[9rem] overflow-y-auto pr-1">
                          {data.byCountry.map((row) => (
                            <div
                              key={row.countryCode}
                              className="flex items-center gap-2"
                            >
                              <span className="text-sm leading-none w-5 text-center shrink-0">
                                {countryFlag(row.countryCode)}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-0.5">
                                  <span className="text-xs font-medium truncate">
                                    {row.country}
                                  </span>
                                  <span className="text-xs tabular-nums text-muted-foreground ml-2 shrink-0">
                                    {row.returnCount.toLocaleString("fr-FR")}
                                  </span>
                                </div>
                                <PercentBar pct={row.percentage} />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>

                    {/* By Carrier */}
                    <section>
                      <h3 className="flex items-center gap-2 text-sm font-semibold mb-3">
                        <Truck className="h-4 w-4 text-muted-foreground" />
                        Transporteurs
                        {data.byCarrier.length > 0 && (
                          <span className="text-xs text-muted-foreground font-normal">
                            top {data.byCarrier.length}
                          </span>
                        )}
                      </h3>
                      {data.byCarrier.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          Aucune donnée
                        </p>
                      ) : (
                        <div className="space-y-2 max-h-[9rem] overflow-y-auto pr-1">
                          {data.byCarrier.map((row) => (
                            <div
                              key={row.carrier}
                              className="flex items-center gap-2"
                            >
                              <span className="text-xs font-mono font-medium w-20 shrink-0 truncate">
                                {row.carrier}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-0.5">
                                  <span className="sr-only">{row.carrier}</span>
                                  <span className="text-xs tabular-nums text-muted-foreground ml-auto shrink-0">
                                    {row.count.toLocaleString("fr-FR")}
                                  </span>
                                </div>
                                <PercentBar pct={row.percentage} />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  </div>
                </div>
              )}

              {/* Meta editor — always visible when shop is selected */}
              <ShopMetaEditor
                key={shop.id}
                shop={shop}
                clientNames={clientNames}
                csmNames={csmNames}
                onSaved={(updated) => onMetaSaved(shop.id, updated)}
              />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ─── Filter Dropdown ──────────────────────────────────────────────────────────

function FilterDropdown({
  label,
  activeCount,
  children,
  onClear,
}: {
  label: string;
  activeCount?: number;
  children: React.ReactNode;
  onClear?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={[
          "flex items-center gap-1.5 rounded-md border bg-background px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted/60",
          open ? "border-violet-400 ring-1 ring-violet-200" : "",
          activeCount ? "border-violet-300" : "",
        ].join(" ")}
      >
        <span className="text-xs">{label}</span>
        {activeCount ? (
          <span className="flex items-center gap-1">
            <span className="rounded-full bg-violet-600 px-1.5 py-px text-[10px] font-semibold text-white leading-none">
              {activeCount}
            </span>
            {onClear && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    onClear?.();
                  }
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-2.5 w-2.5" />
              </span>
            )}
          </span>
        ) : (
          <ChevronDown
            className={`h-3 w-3 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1.5 z-50 min-w-[180px] rounded-lg border bg-background shadow-md py-1.5 animate-in fade-in-0 zoom-in-95">
          {children}
        </div>
      )}
    </div>
  );
}

function DropdownCheckItem({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: () => void;
  children: React.ReactNode;
}) {
  return (
    <label className="flex items-center gap-2.5 px-3 py-1.5 text-sm cursor-pointer hover:bg-muted/60 transition-colors select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-3.5 w-3.5 accent-violet-600 cursor-pointer"
      />
      {children}
    </label>
  );
}

function DropdownRadioItem({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: () => void;
  children: React.ReactNode;
}) {
  return (
    <label
      className={[
        "flex items-center gap-2.5 px-3 py-1.5 text-sm cursor-pointer hover:bg-muted/60 transition-colors select-none",
        checked ? "text-violet-700 font-medium" : "",
      ].join(" ")}
    >
      <span
        className={[
          "h-3.5 w-3.5 rounded-full border-2 flex items-center justify-center shrink-0",
          checked
            ? "border-violet-600 bg-violet-600"
            : "border-muted-foreground/40",
        ].join(" ")}
      >
        {checked && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
      </span>
      <span onClick={onChange}>{children}</span>
    </label>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type TypeFilter = "prod" | "test" | "all";
type StatsFilter = "all" | "in" | "out";
type ActivityFilter = "all" | "active" | "inactive";

const SORT_DEFAULT: SortKey = "returnRequestsLast30Days";
const DIR_DEFAULT: SortDir = "desc";
const TYPE_DEFAULT: TypeFilter = "prod";
const STATS_DEFAULT: StatsFilter = "all";
const ACTIVITY_DEFAULT: ActivityFilter = "all";

function parseStatusFilter(raw: string | null): Set<ClientStatus> {
  if (!raw) return new Set();
  return new Set(raw.split(",").filter(Boolean) as ClientStatus[]);
}

function serializeStatusFilter(set: Set<ClientStatus>): string {
  return [...set].join(",");
}

function shopToSelected(sh: {
  id: string;
  name: string;
  slug: string;
  isTestShop?: boolean | null;
  clientStatus?: string | null;
  isIncludedInStats?: boolean | null;
  clientName?: string | null;
  notes?: string | null;
  csm?: string | null;
  internalLabel?: string | null;
  hasExchanges?: boolean;
  hasGiftCode?: boolean;
  hasWorkflowLogistician?: boolean;
  activeIntegrations?: string[];
  totalOrders?: number;
  totalReturnRequests?: number;
  ordersLast30Days?: number;
  ordersLast7Days?: number;
  ordersLast24Hours?: number;
  returnRequestsLast30Days?: number;
  returnRequestsLast7Days?: number;
  returnRequestsLast24Hours?: number;
  planProductRecommendation?: boolean | null;
  isActive?: boolean | null;
  lastActivityAt?: number | null;
}): SelectedShop {
  return {
    id: sh.id,
    name: sh.name,
    slug: sh.slug,
    isTestShop: sh.isTestShop ?? null,
    clientStatus: (sh.clientStatus as ClientStatus) ?? null,
    isIncludedInStats: sh.isIncludedInStats ?? null,
    clientName: sh.clientName ?? null,
    notes: sh.notes ?? null,
    csm: sh.csm ?? null,
    internalLabel: sh.internalLabel ?? null,
    hasExchanges: sh.hasExchanges ?? false,
    hasGiftCode: sh.hasGiftCode ?? false,
    hasWorkflowLogistician: sh.hasWorkflowLogistician ?? false,
    activeIntegrations: sh.activeIntegrations ?? [],
    totalOrders: sh.totalOrders ?? 0,
    totalReturnRequests: sh.totalReturnRequests ?? 0,
    ordersLast30Days: sh.ordersLast30Days ?? 0,
    ordersLast7Days: sh.ordersLast7Days ?? 0,
    ordersLast24Hours: sh.ordersLast24Hours ?? 0,
    returnRequestsLast30Days: sh.returnRequestsLast30Days ?? 0,
    returnRequestsLast7Days: sh.returnRequestsLast7Days ?? 0,
    returnRequestsLast24Hours: sh.returnRequestsLast24Hours ?? 0,
    planProductRecommendation: sh.planProductRecommendation ?? null,
    isActive: sh.isActive ?? null,
    lastActivityAt: sh.lastActivityAt ?? null,
  };
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdminPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<
    "global" | "carrier-issues" | "replay-creation-actions"
  >("global");
  const {
    data,
    isPending,
    isError,
    refetch: refetchShops,
  } = useSuperAdminShops();
  const {
    data: catalogData,
    isPending: isCatalogPending,
    refetch: refetchCatalog,
  } = useSuperAdminShopsCatalog();
  const refetch = useCallback(() => {
    void refetchShops();
    void refetchCatalog();
  }, [refetchShops, refetchCatalog]);
  const refreshMutation = useRefreshSuperAdminShops();
  const probeCatalogMutation = useProbeAllCatalogCounts();
  const probeShopMutation = useProbeCatalogShopCount();
  const syncCancelledMutation = useSyncCancelledReturnRequestsStats();

  const [probingShopIds, setProbingShopIds] = useState<Set<string>>(new Set());
  const probeSnapshotRef = useRef<Map<string, number | null>>(new Map());
  const probeStartedAtRef = useRef<number | null>(null);
  const probeInitialCountRef = useRef<number>(0);

  const catalogByShopId = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<typeof catalogData>["catalogs"][number]
    >();
    for (const c of catalogData?.catalogs ?? []) {
      map.set(c.shopId, c);
    }
    return map;
  }, [catalogData]);

  const handleProbeShopCount = useCallback(
    (shopId: string) => {
      const cat = catalogByShopId.get(shopId);
      probeSnapshotRef.current.set(
        shopId,
        cat?.catalogProductCountProbedAt ?? null,
      );
      setProbingShopIds((prev) => new Set([...prev, shopId]));
      probeShopMutation.mutate(
        { params: { path: { shopId } } },
        { onSettled: () => void refetch() },
      );
    },
    [catalogByShopId, probeShopMutation, refetch],
  );

  // ── URL-derived filter/sort/search state ──────────────────────────────────
  const search = searchParams.get("q") ?? "";
  const sortKey = (searchParams.get("sort") ?? SORT_DEFAULT) as SortKey;
  const sortDir = (searchParams.get("dir") ?? DIR_DEFAULT) as SortDir;
  const typeFilter = (searchParams.get("type") ?? TYPE_DEFAULT) as TypeFilter;
  const statusFilter = parseStatusFilter(searchParams.get("status"));
  const statsFilter = (searchParams.get("stats") ??
    STATS_DEFAULT) as StatsFilter;
  const featureFilter = new Set<FeatureKey>(
    (searchParams.get("features")?.split(",").filter(Boolean) ??
      []) as FeatureKey[],
  );
  const activityFilter = (searchParams.get("activity") ??
    ACTIVITY_DEFAULT) as ActivityFilter;
  const openShopId = searchParams.get("shop");

  /** Update a single param, removing it when it equals the default. */
  function setParam(key: string, value: string | null, defaultValue?: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!value || value === defaultValue) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  }

  function setSearch(v: string) {
    setParam("q", v || null);
  }
  function setTypeFilter(v: TypeFilter) {
    setParam("type", v, TYPE_DEFAULT);
  }
  function setStatusFilter(v: Set<ClientStatus>) {
    setParam("status", serializeStatusFilter(v) || null);
  }
  function setStatsFilter(v: StatsFilter) {
    setParam("stats", v, STATS_DEFAULT);
  }
  function setFeatureFilter(v: Set<FeatureKey>) {
    setParam("features", [...v].join(",") || null);
  }
  function setActivityFilter(v: ActivityFilter) {
    setParam("activity", v, ACTIVITY_DEFAULT);
  }

  // ── Ephemeral state (not in URL) ──────────────────────────────────────────
  const [lastRefreshedCount, setLastRefreshedCount] = useState<number | null>(
    null,
  );
  // Local patch applied on top of the URL-derived selectedShop (optimistic updates)
  const [shopPatch, setShopPatch] = useState<Partial<SelectedShop> | null>(
    null,
  );
  const [shopPatchId, setShopPatchId] = useState<string | null>(null);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkTest, setBulkTest] = useState<boolean | undefined>(undefined);
  const [bulkStats, setBulkStats] = useState<boolean | undefined>(undefined);
  const [bulkStatus, setBulkStatus] = useState<ClientStatus | "" | undefined>(
    undefined,
  );
  const bulkMutation = useUpdateSuperAdminShopMeta();

  const shops = useMemo(
    () =>
      (data?.shops ?? []).map((shop) => {
        const cat = catalogByShopId.get(shop.id);
        if (!cat) return shop;
        return {
          ...shop,
          catalogProductCount: cat.catalogProductCount ?? null,
          catalogProductCountProbedAt: cat.catalogProductCountProbedAt ?? null,
          isCatalogProductCountApproximate:
            cat.isCatalogProductCountApproximate ?? null,
          catalogArchivedProductCount: cat.catalogArchivedProductCount ?? null,
          isCatalogArchivedProductCountApproximate:
            cat.isCatalogArchivedProductCountApproximate ?? null,
          loadedCatalogProductCount: cat.loadedCatalogProductCount ?? null,
          loadedCatalogArchivedProductCount:
            cat.loadedCatalogArchivedProductCount ?? null,
        };
      }),
    [data?.shops, catalogByShopId],
  );
  const computedAt: number | undefined = data?.computedAt;

  // Poll refetch while a probe batch is in-flight. Stop when set empties or 5min timeout.
  useEffect(() => {
    if (probingShopIds.size === 0) return;
    const interval = window.setInterval(() => {
      void refetch();
    }, 3000);
    const timeout = window.setTimeout(
      () => setProbingShopIds(new Set()),
      5 * 60_000,
    );
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [probingShopIds.size, refetch]);

  // After each refetch, drop shops whose catalogProductCountProbedAt moved past the snapshot.
  useEffect(() => {
    if (probingShopIds.size === 0 || shops.length === 0) return;
    const updated: string[] = [];
    for (const id of probingShopIds) {
      const shop = shops.find((s) => s.id === id) as
        | { id: string; catalogProductCountProbedAt?: number | null }
        | undefined;
      if (!shop) continue;
      const snap = probeSnapshotRef.current.get(id) ?? null;
      const current = shop.catalogProductCountProbedAt ?? null;
      if (current !== snap) updated.push(id);
    }
    if (updated.length > 0) {
      setProbingShopIds((prev) => {
        const next = new Set(prev);
        updated.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, [shops, probingShopIds]);

  /** Sorted, deduplicated list of all client names already assigned to shops. */
  const clientNames = useMemo(() => {
    const names = new Set<string>();
    for (const sh of shops) {
      const n = (sh as { clientName?: string | null }).clientName;
      if (n) names.add(n);
    }
    return [...names].sort((a, b) => a.localeCompare(b, "fr"));
  }, [shops]);

  /** Sorted, deduplicated list of all CSM names already assigned to shops. */
  const csmNames = useMemo(() => {
    const names = new Set<string>();
    for (const sh of shops) {
      const n = (sh as { csm?: string | null }).csm;
      if (n) names.add(n);
    }
    return [...names].sort((a, b) => a.localeCompare(b, "fr"));
  }, [shops]);

  // ── Derive selectedShop from URL shopId + shops data ─────────────────────
  const selectedShop = useMemo(() => {
    if (!openShopId) return null;
    const sh = shops.find((s) => s.id === openShopId);
    if (!sh) return null;
    const base = shopToSelected(sh as Parameters<typeof shopToSelected>[0]);
    // Apply any optimistic patch saved by handleMetaSaved
    if (shopPatchId === openShopId && shopPatch) {
      return { ...base, ...shopPatch };
    }
    return base;
  }, [openShopId, shops, shopPatch, shopPatchId]);

  // Shops matching the structural filters (type + status + stats + features) — drives KPIs
  const kpiShops = useMemo(() => {
    return shops.filter((sh) => {
      if (typeFilter === "prod" && sh.isTestShop) return false;
      if (typeFilter === "test" && !sh.isTestShop) return false;
      if (
        statusFilter.size > 0 &&
        !statusFilter.has(sh.clientStatus as ClientStatus)
      )
        return false;
      if (statsFilter === "in" && sh.isIncludedInStats === false) return false;
      if (statsFilter === "out" && sh.isIncludedInStats !== false) return false;
      if (featureFilter.size > 0) {
        // AND logic: shop must have every selected feature/integration
        const typedSh = sh as typeof sh & {
          hasExchanges?: boolean;
          hasGiftCode?: boolean;
          hasWorkflowLogistician?: boolean;
          activeIntegrations?: string[];
        };
        const shopFeatures = new Set<string>([
          ...(typedSh.hasExchanges ? ["exchanges"] : []),
          ...(typedSh.hasGiftCode ? ["giftCode"] : []),
          ...(typedSh.hasWorkflowLogistician ? ["logistician"] : []),
          ...(typedSh.activeIntegrations ?? []),
        ]);
        if (![...featureFilter].every((f) => shopFeatures.has(f))) return false;
      }
      if (activityFilter === "active" && sh.isActive === false) return false;
      if (activityFilter === "inactive" && sh.isActive !== false) return false;
      return true;
    });
  }, [
    shops,
    typeFilter,
    statusFilter,
    statsFilter,
    featureFilter,
    activityFilter,
  ]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return kpiShops
      .filter((sh) => {
        if (
          !sh.name.toLowerCase().includes(q) &&
          !sh.slug.toLowerCase().includes(q) &&
          !(sh.email ?? "").toLowerCase().includes(q)
        )
          return false;
        return true;
      })
      .sort((a, b) => {
        const va = a[sortKey] ?? 0;
        const vb = b[sortKey] ?? 0;
        if (typeof va === "string" && typeof vb === "string") {
          return sortDir === "asc"
            ? va.localeCompare(vb)
            : vb.localeCompare(va);
        }
        return sortDir === "asc"
          ? (va as number) - (vb as number)
          : (vb as number) - (va as number);
      });
  }, [kpiShops, search, sortKey, sortDir]);

  const totalOrders = filtered.reduce((s, sh) => s + sh.totalOrders, 0);
  const totalOrders30d = filtered.reduce((s, sh) => s + sh.ordersLast30Days, 0);
  const totalOrders7d = filtered.reduce((s, sh) => s + sh.ordersLast7Days, 0);
  const totalOrders24h = filtered.reduce(
    (s, sh) => s + sh.ordersLast24Hours,
    0,
  );
  const totalReturns = filtered.reduce(
    (s, sh) => s + sh.totalReturnRequests,
    0,
  );
  const totalReturns30d = filtered.reduce(
    (s, sh) => s + sh.returnRequestsLast30Days,
    0,
  );
  const totalReturns7d = filtered.reduce(
    (s, sh) => s + sh.returnRequestsLast7Days,
    0,
  );
  const totalReturns24h = filtered.reduce(
    (s, sh) => s + sh.returnRequestsLast24Hours,
    0,
  );
  const activeShops30d = filtered.filter(
    (sh) => sh.returnRequestsLast30Days > 0,
  ).length;
  const activeShops = filtered.filter(
    (sh) => sh.returnRequestsLast7Days > 0,
  ).length;

  const shopsWithExchanges = filtered.filter((sh) => sh.hasExchanges).length;
  const shopsWithGiftCode = filtered.filter((sh) => sh.hasGiftCode).length;
  const shopsWithWorkflowLogistician = filtered.filter(
    (sh) => sh.hasWorkflowLogistician,
  ).length;
  const total = filtered.length || 1; // avoid division by zero

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setParam("dir", sortDir === "asc" ? "desc" : "asc", DIR_DEFAULT);
    } else {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("sort", key);
          next.delete("dir"); // reset to default desc
          return next;
        },
        { replace: true },
      );
    }
  }

  function SortButton({ label, col }: { label: string; col: SortKey }) {
    return (
      <button
        onClick={() => toggleSort(col)}
        className="flex items-center gap-1 hover:text-foreground transition-colors"
      >
        {label}
        <ArrowUpDown
          className={`h-3 w-3 ${sortKey === col ? "text-violet-500" : "opacity-40"}`}
        />
      </button>
    );
  }

  const INCREMENTAL_MAX_AGE_MS = 15 * 60 * 1000; // 15 min

  async function handleRefresh(force = false) {
    const result = await refreshMutation.mutateAsync(
      force ? {} : { query: { maxAgeMs: String(INCREMENTAL_MAX_AGE_MS) } },
    );
    setLastRefreshedCount(result?.refreshedCount ?? null);
    await refetch();
  }

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const openShop = useCallback(
    (shop: SelectedShop) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("shop", shop.id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  function closeShop() {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("shop");
        return next;
      },
      { replace: true },
    );
  }

  function toggleSelectAll() {
    if (filtered.every((sh) => selectedIds.has(sh.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((sh) => sh.id)));
    }
  }

  async function handleBulkApply() {
    const body: {
      isTestShop?: boolean | null;
      isIncludedInStats?: boolean | null;
      clientStatus?: string | null;
    } = {};
    if (bulkTest !== undefined) body.isTestShop = bulkTest || null;
    if (bulkStats !== undefined)
      body.isIncludedInStats = bulkStats ? null : false;
    if (bulkStatus !== undefined) body.clientStatus = bulkStatus || null;

    await Promise.all(
      Array.from(selectedIds).map((id) =>
        bulkMutation.mutateAsync({
          params: { path: { shopId: id } },
          body,
        }),
      ),
    );

    setSelectedIds(new Set());
    setBulkTest(undefined);
    setBulkStats(undefined);
    setBulkStatus(undefined);
    await refetch();
  }

  async function handleMetaSaved(
    shopId: string,
    updated: Partial<SelectedShop>,
  ): Promise<void> {
    setShopPatch(updated);
    setShopPatchId(shopId);
    await refetch();
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour
            </button>
            <div className="h-4 w-px bg-border" />
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-violet-500" />
              <h1 className="text-lg font-semibold">Super Admin</h1>
              <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">
                Baback internal
              </span>
            </div>
          </div>

          {/* Cache freshness + refresh */}
          <div className="flex items-center gap-3">
            {computedAt && (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                Données calculées {formatRelative(computedAt)}
              </span>
            )}
            {lastRefreshedCount !== null && !refreshMutation.isPending && (
              <span className="text-xs text-muted-foreground">
                {lastRefreshedCount === 0
                  ? "Tout à jour"
                  : `${lastRefreshedCount} shop${lastRefreshedCount > 1 ? "s" : ""} recalculé${lastRefreshedCount > 1 ? "s" : ""}`}
              </span>
            )}
            <button
              onClick={() => syncCancelledMutation.mutate({})}
              disabled={syncCancelledMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border hover:bg-muted transition-colors disabled:opacity-50"
              title="Backfill isCancelled dans statisticsReturnRequestFlattened pour tous les shops (idempotent)"
            >
              <RotateCcw
                className={`h-3 w-3 ${syncCancelledMutation.isPending ? "animate-spin" : ""}`}
              />
              {syncCancelledMutation.isPending
                ? "Sync…"
                : syncCancelledMutation.isSuccess
                  ? `✓ ${(syncCancelledMutation.data as { updated: number } | undefined)?.updated ?? 0} màj`
                  : "Sync annulations"}
            </button>
            <button
              onClick={() =>
                probeCatalogMutation.mutate(
                  {},
                  {
                    onSuccess: () => {
                      const snap = new Map<string, number | null>();
                      for (const s of shops) {
                        const probedAt = (
                          s as { catalogProductCountProbedAt?: number | null }
                        ).catalogProductCountProbedAt;
                        snap.set(s.id, probedAt ?? null);
                      }
                      probeSnapshotRef.current = snap;
                      probeStartedAtRef.current = Date.now();
                      const ids = new Set(shops.map((s) => s.id));
                      probeInitialCountRef.current = ids.size;
                      setProbingShopIds(ids);
                    },
                  },
                )
              }
              disabled={
                probeCatalogMutation.isPending || probingShopIds.size > 0
              }
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border hover:bg-muted transition-colors disabled:opacity-50"
              title="Lance un probe productCount sur tous les shops (1 appel GQL par shop, tâche de fond)"
            >
              <Package
                className={`h-3 w-3 ${probeCatalogMutation.isPending || probingShopIds.size > 0 ? "animate-spin" : ""}`}
              />
              {probingShopIds.size > 0
                ? `Probe ${probeInitialCountRef.current - probingShopIds.size}/${probeInitialCountRef.current}`
                : probeCatalogMutation.isPending
                  ? "Envoi…"
                  : "Probe catalogs"}
            </button>
            <div className="flex items-center rounded-md border overflow-hidden">
              <button
                onClick={() => handleRefresh(false)}
                disabled={refreshMutation.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors disabled:opacity-50"
                title="Recalcule uniquement les shops dont les stats ont plus de 15 min"
              >
                <RefreshCw
                  className={`h-3 w-3 ${refreshMutation.isPending ? "animate-spin" : ""}`}
                />
                {refreshMutation.isPending ? "Calcul…" : "Rafraîchir"}
              </button>
              <div className="w-px h-5 bg-border" />
              <button
                onClick={() => handleRefresh(true)}
                disabled={refreshMutation.isPending}
                className="px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
                title="Recalcule tous les shops sans exception"
              >
                Tout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tab bar */}
      <div className="border-b bg-background sticky top-[73px] z-[9]">
        <div className="mx-auto max-w-screen-2xl px-6 flex gap-0">
          {(
            [
              { key: "global", label: "Global", icon: null },
              {
                key: "carrier-issues",
                label: "Problèmes carriers",
                icon: AlertTriangle,
              },
              {
                key: "replay-creation-actions",
                label: "Rejouer actions",
                icon: RotateCcw,
              },
            ] as const
          ).map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={[
                "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                activeTab === key
                  ? "border-violet-500 text-violet-600"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border",
              ].join(" ")}
            >
              {Icon && (
                <Icon
                  className={`h-3.5 w-3.5 ${activeTab === key ? "text-violet-500" : "text-muted-foreground"}`}
                />
              )}
              {label}
            </button>
          ))}
        </div>
      </div>

      <main
        className={[
          "mx-auto max-w-screen-2xl px-6 py-8 space-y-6 transition-[padding]",
          selectedIds.size > 0 ? "pb-28" : "",
        ].join(" ")}
      >
        {/* ── Replay Creation Actions tab ── */}
        {activeTab === "replay-creation-actions" && (
          <ReplayCreationActionsTab />
        )}

        {/* ── Carrier Issues tab ── */}
        {activeTab === "carrier-issues" && (
          <CarrierIssuesTab
            shops={shops.map((s) => ({
              id: s.id,
              name: (s as { name?: string }).name ?? s.id,
              slug: (s as { slug?: string }).slug ?? "",
            }))}
          />
        )}

        {/* ── Global tab (existing content) ── */}
        {activeTab === "global" && (
          <>
            {/* Filters + Search */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Search — first */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Rechercher un shop…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-64 rounded-md border bg-background pl-9 pr-3 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>

              <div className="w-px h-5 bg-border" />

              {/* Type filter — single-select dropdown */}
              <FilterDropdown
                label={
                  typeFilter === "prod"
                    ? "Prod"
                    : typeFilter === "test"
                      ? "Test"
                      : "Type : Tous"
                }
              >
                {(["prod", "test", "all"] as TypeFilter[]).map((v) => (
                  <DropdownRadioItem
                    key={v}
                    checked={typeFilter === v}
                    onChange={() => setTypeFilter(v)}
                  >
                    {v === "prod" ? (
                      "Production"
                    ) : v === "test" ? (
                      <span className="flex items-center gap-1.5">
                        <FlaskConical className="h-3 w-3" /> Test
                      </span>
                    ) : (
                      "Tous"
                    )}
                  </DropdownRadioItem>
                ))}
              </FilterDropdown>

              {/* Status filter — multi-select dropdown */}
              <FilterDropdown
                label="Statut"
                activeCount={statusFilter.size || undefined}
                onClear={() => setStatusFilter(new Set())}
              >
                {(
                  [
                    "prospect",
                    "onboarding",
                    "active",
                    "paused",
                    "churned",
                  ] as const
                ).map((s) => (
                  <DropdownCheckItem
                    key={s}
                    checked={statusFilter.has(s)}
                    onChange={() => {
                      const next = new Set(statusFilter);
                      if (next.has(s)) next.delete(s);
                      else next.add(s);
                      setStatusFilter(next);
                    }}
                  >
                    <span
                      className={`rounded-full px-2 py-px text-[10px] font-medium ${CLIENT_STATUS_LABELS[s].color}`}
                    >
                      {CLIENT_STATUS_LABELS[s].label}
                    </span>
                  </DropdownCheckItem>
                ))}
              </FilterDropdown>

              {/* Stats filter — single-select dropdown */}
              <FilterDropdown
                label={
                  statsFilter === "in"
                    ? "Stats : Inclus"
                    : statsFilter === "out"
                      ? "Stats : Exclus"
                      : "Stats"
                }
              >
                {(["all", "in", "out"] as StatsFilter[]).map((v) => (
                  <DropdownRadioItem
                    key={v}
                    checked={statsFilter === v}
                    onChange={() => setStatsFilter(v)}
                  >
                    {v === "all" ? "Tous" : v === "in" ? "Inclus" : "Exclus"}
                  </DropdownRadioItem>
                ))}
              </FilterDropdown>

              {/* Activity filter — single-select dropdown */}
              <FilterDropdown
                label={
                  activityFilter === "active"
                    ? "Actifs"
                    : activityFilter === "inactive"
                      ? "Inactifs"
                      : "Activité"
                }
              >
                {(["all", "active", "inactive"] as ActivityFilter[]).map(
                  (v) => (
                    <DropdownRadioItem
                      key={v}
                      checked={activityFilter === v}
                      onChange={() => setActivityFilter(v)}
                    >
                      {v === "all"
                        ? "Tous"
                        : v === "active"
                          ? "Actifs"
                          : "Inactifs"}
                    </DropdownRadioItem>
                  ),
                )}
              </FilterDropdown>

              {/* Features & integrations — multi-select dropdown */}
              <FilterDropdown
                label="Features"
                activeCount={featureFilter.size || undefined}
                onClear={() => setFeatureFilter(new Set())}
              >
                {/* Core features */}
                <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Fonctionnalités
                </div>
                {(
                  [
                    {
                      key: "exchanges" as FeatureKey,
                      label: "Échanges",
                      icon: (
                        <ArrowLeftRight className="h-3 w-3 text-blue-500" />
                      ),
                    },
                    {
                      key: "giftCode" as FeatureKey,
                      label: "Avoirs",
                      icon: <Gift className="h-3 w-3 text-violet-500" />,
                    },
                    {
                      key: "logistician" as FeatureKey,
                      label: "Workflow logisticien",
                      icon: <Package className="h-3 w-3 text-orange-500" />,
                    },
                  ] as const
                ).map(({ key, label, icon }) => (
                  <DropdownCheckItem
                    key={key}
                    checked={featureFilter.has(key)}
                    onChange={() => {
                      const next = new Set(featureFilter);
                      if (next.has(key)) next.delete(key);
                      else next.add(key);
                      setFeatureFilter(next);
                    }}
                  >
                    <span className="flex items-center gap-1.5">
                      {icon}
                      {label}
                    </span>
                  </DropdownCheckItem>
                ))}
                {/* Integrations */}
                <div className="mx-3 my-1 h-px bg-border" />
                <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Intégrations
                </div>
                {Object.entries(INTEGRATIONS).map(
                  ([key, { label, pillColor }]) => (
                    <DropdownCheckItem
                      key={key}
                      checked={featureFilter.has(key as FeatureKey)}
                      onChange={() => {
                        const next = new Set(featureFilter);
                        const k = key as FeatureKey;
                        if (next.has(k)) next.delete(k);
                        else next.add(k);
                        setFeatureFilter(next);
                      }}
                    >
                      <span className="flex items-center gap-1.5">
                        <span
                          className={`inline-block w-2 h-2 rounded-full shrink-0 ${pillColor}`}
                        />
                        {label}
                      </span>
                    </DropdownCheckItem>
                  ),
                )}
              </FilterDropdown>
            </div>

            {/* KPI section */}
            <div className="flex gap-3 items-stretch">
              {/* Shops block */}
              <div className="flex flex-col gap-3 w-36 shrink-0">
                <KpiCard
                  label="Shops"
                  value={filtered.length}
                  icon={<Package className="h-4 w-4 text-muted-foreground" />}
                />
                <KpiCard
                  label="Actifs 30j"
                  value={activeShops30d}
                  icon={<TrendingUp className="h-4 w-4 text-amber-500" />}
                  highlight="amber"
                />
                <KpiCard
                  label="Actifs 7j"
                  value={activeShops}
                  icon={<TrendingUp className="h-4 w-4 text-emerald-500" />}
                  highlight="emerald"
                />
              </div>

              {/* Period stat panels */}
              <div className="flex-1 grid grid-cols-4 gap-3">
                <StatPanel
                  period="Total"
                  orders={totalOrders}
                  returns={totalReturns}
                />
                <StatPanel
                  period="30 jours"
                  orders={totalOrders30d}
                  returns={totalReturns30d}
                  color="amber"
                />
                <StatPanel
                  period="7 jours"
                  orders={totalOrders7d}
                  returns={totalReturns7d}
                  color="emerald"
                />
                <StatPanel
                  period="24h"
                  orders={totalOrders24h}
                  returns={totalReturns24h}
                  color="blue"
                />
              </div>
            </div>

            {/* Feature KPIs */}
            <div className="grid grid-cols-3 gap-3">
              <FeatureKpi
                icon={<ArrowLeftRight className="h-4 w-4 text-blue-500" />}
                label="Échanges activés"
                count={shopsWithExchanges}
                total={filtered.length}
                pct={Math.round((shopsWithExchanges / total) * 100)}
                color="blue"
              />
              <FeatureKpi
                icon={<Gift className="h-4 w-4 text-violet-500" />}
                label="Avoirs activés"
                count={shopsWithGiftCode}
                total={filtered.length}
                pct={Math.round((shopsWithGiftCode / total) * 100)}
                color="violet"
              />
              <FeatureKpi
                icon={<Package className="h-4 w-4 text-orange-500" />}
                label="Workflow logisticien"
                count={shopsWithWorkflowLogistician}
                total={filtered.length}
                pct={Math.round((shopsWithWorkflowLogistician / total) * 100)}
                color="orange"
              />
            </div>

            {/* Table */}
            <div className="rounded-lg border bg-background overflow-hidden overflow-x-auto">
              {isPending ? (
                <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
                  Chargement…
                </div>
              ) : isError ? (
                <div className="flex items-center justify-center py-20 text-destructive text-sm">
                  Erreur lors du chargement des données
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
                  Aucun shop trouvé
                </div>
              ) : (
                <table className="w-full min-w-[1400px] text-sm">
                  <thead className="bg-muted/50">
                    {/* Period group headers */}
                    <tr className="border-b border-border/50">
                      <th className="px-4 py-1.5 text-left" colSpan={3} />
                      <th className="hidden lg:table-cell" />
                      <th className="hidden lg:table-cell" />
                      <th
                        className="px-3 py-1.5 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 hidden xl:table-cell border-l border-border/50"
                        colSpan={2}
                      >
                        Total
                      </th>
                      <th
                        className="px-3 py-1.5 text-center text-[10px] font-semibold uppercase tracking-wider text-amber-600 hidden lg:table-cell border-l border-amber-200 bg-amber-50/60"
                        colSpan={2}
                      >
                        30j
                      </th>
                      <th
                        className="px-3 py-1.5 text-center text-[10px] font-semibold uppercase tracking-wider text-emerald-600 hidden md:table-cell border-l border-emerald-200 bg-emerald-50/60"
                        colSpan={2}
                      >
                        7j
                      </th>
                      <th
                        className="px-3 py-1.5 text-center text-[10px] font-semibold uppercase tracking-wider text-blue-500 border-l border-blue-200"
                        colSpan={2}
                      >
                        24h
                      </th>
                      <th className="hidden lg:table-cell" colSpan={2} />
                    </tr>
                    {/* Column headers */}
                    <tr className="border-b">
                      <th className="w-10 px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={
                            filtered.length > 0 &&
                            filtered.every((sh) => selectedIds.has(sh.id))
                          }
                          ref={(el) => {
                            if (el)
                              el.indeterminate =
                                selectedIds.size > 0 &&
                                !filtered.every((sh) => selectedIds.has(sh.id));
                          }}
                          onChange={toggleSelectAll}
                          className="h-4 w-4 cursor-pointer accent-violet-600"
                        />
                      </th>
                      <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                        <SortButton label="Shop" col="name" />
                      </th>
                      <th className="px-4 py-2 text-left font-medium text-muted-foreground hidden md:table-cell">
                        Plan
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-muted-foreground hidden lg:table-cell">
                        <SortButton label="Catalog" col="catalogProductCount" />
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground hidden lg:table-cell">
                        Features
                      </th>
                      {/* Total */}
                      <th className="px-3 py-2 text-right font-medium text-muted-foreground hidden xl:table-cell border-l border-border/50">
                        <SortButton label="Ret" col="totalReturnRequests" />
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-muted-foreground hidden xl:table-cell">
                        <SortButton label="Cmd" col="totalOrders" />
                      </th>
                      {/* 30j */}
                      <th className="px-3 py-2 text-right font-medium text-amber-600 hidden lg:table-cell border-l border-amber-200 bg-amber-50/60">
                        <SortButton
                          label="Ret"
                          col="returnRequestsLast30Days"
                        />
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-amber-600 hidden lg:table-cell bg-amber-50/60">
                        <SortButton label="Cmd" col="ordersLast30Days" />
                      </th>
                      {/* 7j */}
                      <th className="px-3 py-2 text-right font-medium text-emerald-600 hidden md:table-cell border-l border-emerald-200 bg-emerald-50/60">
                        <SortButton label="Ret" col="returnRequestsLast7Days" />
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-emerald-600 hidden md:table-cell bg-emerald-50/60">
                        <SortButton label="Cmd" col="ordersLast7Days" />
                      </th>
                      {/* 24h */}
                      <th className="px-3 py-2 text-right font-medium text-blue-500 border-l border-blue-200">
                        <SortButton
                          label="Ret"
                          col="returnRequestsLast24Hours"
                        />
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-blue-500">
                        <SortButton label="Cmd" col="ordersLast24Hours" />
                      </th>
                      <th className="px-3 py-2 text-center font-medium text-muted-foreground hidden lg:table-cell">
                        Activité
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-muted-foreground hidden xl:table-cell">
                        <SortButton
                          label="Dernière activité"
                          col="lastActivityAt"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filtered.map((shop) => (
                      <ShopRow
                        key={shop.id}
                        shop={shop as ShopRowData}
                        isSelected={selectedIds.has(shop.id)}
                        isProbingCatalog={probingShopIds.has(shop.id)}
                        isCatalogPending={isCatalogPending}
                        onToggle={toggleSelect}
                        onOpen={openShop}
                        onProbeCount={handleProbeShopCount}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <p className="text-xs text-muted-foreground text-center">
              {filtered.length} shop{filtered.length > 1 ? "s" : ""} · Cliquer
              pour voir le détail
            </p>
          </>
        )}
      </main>

      {/* Detail Drawer */}
      <ShopDetailDrawer
        shop={selectedShop}
        clientNames={clientNames}
        csmNames={csmNames}
        onClose={closeShop}
        onMetaSaved={handleMetaSaved}
      />

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <BulkActionBar
          count={selectedIds.size}
          bulkTest={bulkTest}
          onBulkTest={setBulkTest}
          bulkStats={bulkStats}
          onBulkStats={setBulkStats}
          bulkStatus={bulkStatus}
          onBulkStatus={setBulkStatus}
          isPending={bulkMutation.isPending}
          onApply={handleBulkApply}
          onClear={() => {
            setSelectedIds(new Set());
            setBulkTest(undefined);
            setBulkStats(undefined);
            setBulkStatus(undefined);
          }}
        />
      )}
    </div>
  );
}

// ─── Shop Row (memoized — only re-renders when isSelected changes) ────────────

const ShopRow = memo(function ShopRow({
  shop,
  isSelected,
  isProbingCatalog,
  isCatalogPending,
  onToggle,
  onOpen,
  onProbeCount,
}: {
  shop: ShopRowData;
  isSelected: boolean;
  isProbingCatalog: boolean;
  isCatalogPending: boolean;
  onToggle: (id: string) => void;
  onOpen: (shop: SelectedShop) => void;
  onProbeCount: (shopId: string) => void;
}) {
  return (
    <tr
      onClick={() =>
        onOpen({
          id: shop.id,
          name: shop.name,
          slug: shop.slug,
          isTestShop: shop.isTestShop ?? null,
          clientName: shop.clientName ?? null,
          clientStatus: (shop.clientStatus as ClientStatus) ?? null,
          isIncludedInStats: shop.isIncludedInStats ?? null,
          notes: shop.notes ?? null,
          csm: shop.csm ?? null,
          internalLabel: shop.internalLabel ?? null,
          hasExchanges: shop.hasExchanges,
          hasGiftCode: shop.hasGiftCode,
          hasWorkflowLogistician: shop.hasWorkflowLogistician,
          activeIntegrations: shop.activeIntegrations,
          totalOrders: shop.totalOrders,
          totalReturnRequests: shop.totalReturnRequests,
          ordersLast30Days: shop.ordersLast30Days,
          ordersLast7Days: shop.ordersLast7Days,
          ordersLast24Hours: shop.ordersLast24Hours,
          returnRequestsLast30Days: shop.returnRequestsLast30Days,
          returnRequestsLast7Days: shop.returnRequestsLast7Days,
          returnRequestsLast24Hours: shop.returnRequestsLast24Hours,
          planProductRecommendation: shop.planProductRecommendation ?? null,
          isActive: shop.isActive ?? null,
          lastActivityAt: shop.lastActivityAt ?? null,
        })
      }
      className={[
        "cursor-pointer hover:bg-muted/40 transition-colors",
        isSelected ? "bg-violet-50" : "",
      ].join(" ")}
    >
      <td
        className="w-10 px-3 py-3 text-center"
        onClick={(e) => {
          e.stopPropagation();
          onToggle(shop.id);
        }}
      >
        <input
          type="checkbox"
          checked={isSelected}
          readOnly
          className="h-4 w-4 cursor-pointer accent-violet-600 pointer-events-none"
        />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-medium">{shop.name}</span>
          {shop.isActive === false && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-600">
              <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
              inactif
            </span>
          )}
          {shop.isTestShop && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
              <FlaskConical className="h-2.5 w-2.5" />
              test
            </span>
          )}
          {shop.clientStatus && (
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${CLIENT_STATUS_LABELS[shop.clientStatus as ClientStatus]?.color ?? "bg-gray-100 text-gray-500"}`}
            >
              {CLIENT_STATUS_LABELS[shop.clientStatus as ClientStatus]?.label ??
                shop.clientStatus}
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">{shop.slug}</div>
      </td>
      <td className="px-4 py-3 hidden md:table-cell">
        {shop.planCode ? (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-mono">
            {shop.planCode}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      {/* Catalog product count */}
      <td className="px-3 py-3 text-right hidden lg:table-cell tabular-nums">
        <div className="inline-flex items-center justify-end gap-1 group/catalog">
          {isProbingCatalog || isCatalogPending ? (
            <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />
          ) : shop.catalogProductCount !== null &&
            shop.catalogProductCount !== undefined ? (
            <div
              title={
                shop.catalogProductCountProbedAt
                  ? `dernier sync: ${formatRelative(shop.catalogProductCountProbedAt)}`
                  : undefined
              }
              className="text-xs font-medium whitespace-nowrap flex flex-col items-end gap-0.5"
            >
              <span>
                {shop.loadedCatalogProductCount !== null &&
                shop.loadedCatalogProductCount !== undefined
                  ? `${new Intl.NumberFormat("fr-FR").format(shop.loadedCatalogProductCount)} / `
                  : ""}
                {shop.isCatalogProductCountApproximate
                  ? `+ de ${new Intl.NumberFormat("fr-FR", { notation: "compact" }).format(shop.catalogProductCount)}`
                  : new Intl.NumberFormat("fr-FR").format(
                      shop.catalogProductCount,
                    )}
              </span>
              {shop.catalogArchivedProductCount !== null &&
              shop.catalogArchivedProductCount !== undefined ? (
                <span className="text-muted-foreground font-normal">
                  {shop.loadedCatalogArchivedProductCount !== null &&
                  shop.loadedCatalogArchivedProductCount !== undefined
                    ? `${new Intl.NumberFormat("fr-FR").format(shop.loadedCatalogArchivedProductCount)} / `
                    : ""}
                  {shop.isCatalogArchivedProductCountApproximate
                    ? `+ de ${new Intl.NumberFormat("fr-FR", { notation: "compact" }).format(shop.catalogArchivedProductCount)}`
                    : new Intl.NumberFormat("fr-FR").format(
                        shop.catalogArchivedProductCount,
                      )}{" "}
                  arch.
                </span>
              ) : null}
            </div>
          ) : (
            <span className="text-muted-foreground text-xs">—</span>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onProbeCount(shop.id);
            }}
            disabled={isProbingCatalog}
            className="opacity-0 group-hover/catalog:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted disabled:opacity-0"
            title="Relancer le probe catalog pour ce shop"
          >
            <RefreshCw className="h-3 w-3 text-muted-foreground" />
          </button>
        </div>
      </td>
      {/* Feature flags */}
      <td className="px-3 py-3 hidden lg:table-cell">
        <div className="flex items-center gap-1 flex-wrap">
          <span
            title="Échanges"
            className={[
              "inline-flex items-center rounded px-1 py-0.5 text-[10px] font-medium",
              shop.hasExchanges
                ? "bg-blue-100 text-blue-700"
                : "bg-muted text-muted-foreground/40",
            ].join(" ")}
          >
            <ArrowLeftRight className="h-2.5 w-2.5" />
          </span>
          <span
            title="Avoirs / Bons d'achat"
            className={[
              "inline-flex items-center rounded px-1 py-0.5 text-[10px] font-medium",
              shop.hasGiftCode
                ? "bg-violet-100 text-violet-700"
                : "bg-muted text-muted-foreground/40",
            ].join(" ")}
          >
            <Gift className="h-2.5 w-2.5" />
          </span>
          <span
            title="Workflow logisticien"
            className={[
              "inline-flex items-center rounded px-1 py-0.5 text-[10px] font-medium",
              shop.hasWorkflowLogistician
                ? "bg-orange-100 text-orange-700"
                : "bg-muted text-muted-foreground/40",
            ].join(" ")}
          >
            <Package className="h-2.5 w-2.5" />
          </span>
          {shop.activeIntegrations.map((k) => {
            const info = INTEGRATIONS[k];
            return (
              <span
                key={k}
                title={info?.label ?? k}
                className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${info?.pillColor ?? "bg-muted-foreground"}`}
              />
            );
          })}
        </div>
      </td>
      {/* Total */}
      <td className="px-3 py-3 text-right tabular-nums hidden xl:table-cell text-muted-foreground border-l border-border/50">
        {shop.totalReturnRequests.toLocaleString("fr-FR")}
      </td>
      <td className="px-3 py-3 text-right tabular-nums hidden xl:table-cell text-muted-foreground">
        {shop.totalOrders.toLocaleString("fr-FR")}
      </td>
      {/* 30j */}
      <td className="px-3 py-3 text-right tabular-nums hidden lg:table-cell text-amber-700 border-l border-amber-200 bg-amber-50/20">
        {shop.returnRequestsLast30Days.toLocaleString("fr-FR")}
      </td>
      <td className="px-3 py-3 text-right tabular-nums hidden lg:table-cell text-amber-700 bg-amber-50/20">
        {shop.ordersLast30Days.toLocaleString("fr-FR")}
      </td>
      {/* 7j */}
      <td className="px-3 py-3 text-right tabular-nums hidden md:table-cell text-emerald-700 border-l border-emerald-200 bg-emerald-50/20">
        {shop.returnRequestsLast7Days.toLocaleString("fr-FR")}
      </td>
      <td className="px-3 py-3 text-right tabular-nums hidden md:table-cell text-emerald-700 bg-emerald-50/20">
        {shop.ordersLast7Days.toLocaleString("fr-FR")}
      </td>
      {/* 24h */}
      <td className="px-3 py-3 text-right tabular-nums font-medium text-blue-700 border-l border-blue-200">
        {shop.returnRequestsLast24Hours.toLocaleString("fr-FR")}
      </td>
      <td className="px-3 py-3 text-right tabular-nums font-medium text-blue-700">
        {shop.ordersLast24Hours.toLocaleString("fr-FR")}
      </td>
      <td className="px-3 py-3 text-center hidden lg:table-cell">
        <ActivityBadge last7Days={shop.returnRequestsLast7Days} />
      </td>
      <td className="px-3 py-3 text-right text-muted-foreground hidden xl:table-cell">
        {formatDate(shop.lastActivityAt)}
      </td>
    </tr>
  );
});

// ─── Bulk Action Bar ──────────────────────────────────────────────────────────

function TriPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        "rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
        active
          ? "bg-violet-600 text-white"
          : "bg-muted text-muted-foreground hover:bg-muted/80",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

function BulkActionBar({
  count,
  bulkTest,
  onBulkTest,
  bulkStats,
  onBulkStats,
  bulkStatus,
  onBulkStatus,
  isPending,
  onApply,
  onClear,
}: {
  count: number;
  bulkTest: boolean | undefined;
  onBulkTest: (v: boolean | undefined) => void;
  bulkStats: boolean | undefined;
  onBulkStats: (v: boolean | undefined) => void;
  bulkStatus: ClientStatus | "" | undefined;
  onBulkStatus: (v: ClientStatus | "" | undefined) => void;
  isPending: boolean;
  onApply: () => void;
  onClear: () => void;
}) {
  const hasChanges =
    bulkTest !== undefined ||
    bulkStats !== undefined ||
    bulkStatus !== undefined;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-30 border-t bg-background shadow-[0_-4px_16px_rgba(0,0,0,0.08)]">
      <div className="mx-auto max-w-7xl px-6 py-3 flex items-center gap-5">
        {/* Count */}
        <span className="text-sm font-semibold tabular-nums shrink-0">
          {count} shop{count > 1 ? "s" : ""} sélectionné{count > 1 ? "s" : ""}
        </span>

        <div className="w-px h-5 bg-border shrink-0" />

        {/* Test */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Test</span>
          <TriPill
            label="—"
            active={bulkTest === undefined}
            onClick={() => onBulkTest(undefined)}
          />
          <TriPill
            label="Oui"
            active={bulkTest === true}
            onClick={() => onBulkTest(true)}
          />
          <TriPill
            label="Non"
            active={bulkTest === false}
            onClick={() => onBulkTest(false)}
          />
        </div>

        <div className="w-px h-5 bg-border shrink-0" />

        {/* Stats */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Stats</span>
          <TriPill
            label="—"
            active={bulkStats === undefined}
            onClick={() => onBulkStats(undefined)}
          />
          <TriPill
            label="Inclus"
            active={bulkStats === true}
            onClick={() => onBulkStats(true)}
          />
          <TriPill
            label="Exclus"
            active={bulkStats === false}
            onClick={() => onBulkStats(false)}
          />
        </div>

        <div className="w-px h-5 bg-border shrink-0" />

        {/* Status */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Statut</span>
          <select
            value={bulkStatus === undefined ? "__unchanged__" : bulkStatus}
            onChange={(e) => {
              const v = e.target.value;
              onBulkStatus(
                v === "__unchanged__" ? undefined : (v as ClientStatus | ""),
              );
            }}
            className="rounded-md border bg-background px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="__unchanged__">— Inchangé</option>
            <option value="">Effacer</option>
            <option value="prospect">Prospect</option>
            <option value="onboarding">Onboarding</option>
            <option value="active">Actif</option>
            <option value="paused">Pausé</option>
            <option value="churned">Churné</option>
          </select>
        </div>

        {/* Actions */}
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <button
            onClick={onClear}
            className="rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
          >
            Désélectionner
          </button>
          <button
            onClick={onApply}
            disabled={!hasChanges || isPending}
            className="rounded-md bg-violet-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-violet-700 transition-colors disabled:opacity-40"
          >
            {isPending ? "Enregistrement…" : "Appliquer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  icon,
  highlight,
  small,
}: {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  highlight?: "emerald" | "blue" | "amber";
  small?: boolean;
}) {
  const borderClass =
    highlight === "emerald"
      ? "border-emerald-200 border-l-4 border-l-emerald-400"
      : highlight === "blue"
        ? "border-blue-200 border-l-4 border-l-blue-500"
        : highlight === "amber"
          ? "border-amber-200 border-l-4 border-l-amber-400"
          : "";
  const textClass =
    highlight === "emerald"
      ? "text-emerald-700"
      : highlight === "blue"
        ? "text-blue-700"
        : highlight === "amber"
          ? "text-amber-700"
          : "";

  return (
    <div className={`rounded-lg border bg-background p-3 ${borderClass}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-muted-foreground">
          {label}
        </span>
        {icon}
      </div>
      <div
        className={`tabular-nums font-bold ${small ? "text-lg" : "text-2xl"} ${textClass}`}
      >
        {value}
      </div>
    </div>
  );
}

// ─── Stat Panel ───────────────────────────────────────────────────────────────

const PERIOD_COLORS = {
  amber: {
    border: "border border-amber-200 border-l-4 border-l-amber-400",
    headerBg: "bg-amber-50/60",
    label: "text-amber-600",
    num: "text-amber-700",
    rate: "text-amber-400",
  },
  emerald: {
    border: "border border-emerald-200 border-l-4 border-l-emerald-400",
    headerBg: "bg-emerald-50/60",
    label: "text-emerald-600",
    num: "text-emerald-700",
    rate: "text-emerald-400",
  },
  blue: {
    border: "border border-blue-200 border-l-4 border-l-blue-500",
    headerBg: "bg-blue-50/60",
    label: "text-blue-600",
    num: "text-blue-700",
    rate: "text-blue-400",
  },
} as const;

function StatPanel({
  period,
  orders,
  returns,
  color,
}: {
  period: string;
  orders: number;
  returns: number;
  color?: keyof typeof PERIOD_COLORS;
}) {
  const rate = orders > 0 ? ((returns / orders) * 100).toFixed(1) : null;
  const c = color ? PERIOD_COLORS[color] : null;

  return (
    <div
      className={[
        "rounded-lg bg-background flex flex-col overflow-hidden",
        c ? c.border : "border",
      ].join(" ")}
    >
      {/* Header */}
      <div
        className={`flex items-center justify-between px-5 pt-3 pb-2 ${c ? c.headerBg : ""}`}
      >
        <span
          className={`text-xs font-bold uppercase tracking-wider ${c ? c.label : "text-muted-foreground"}`}
        >
          {period}
        </span>
        {rate && (
          <span
            className={`text-xs tabular-nums ${c ? c.rate : "text-muted-foreground/50"}`}
          >
            {rate}%
          </span>
        )}
      </div>

      {/* Stacked metrics — RET on top, CMD below */}
      <div className="flex-1 flex flex-col items-center justify-center gap-6 py-5">
        <div className="flex flex-col items-center gap-1">
          <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            <RotateCcw className="h-3 w-3" />
            Ret
          </span>
          <span
            className={`text-3xl font-bold tabular-nums leading-none ${c ? c.num : "text-foreground"}`}
          >
            {returns.toLocaleString("fr-FR")}
          </span>
        </div>

        <div className="flex flex-col items-center gap-1">
          <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            <Package className="h-3 w-3" />
            Cmd
          </span>
          <span
            className={`text-3xl font-bold tabular-nums leading-none ${c ? c.num : "text-foreground"}`}
          >
            {orders.toLocaleString("fr-FR")}
          </span>
        </div>
      </div>
    </div>
  );
}
